import type { AgentKind } from '@app/shared';

/**
 * Per-kind default episode limits for the Agent Builder (#116).
 *
 * The SDK spends a MODEL TURN per tool interaction — each ToolSearch to load a tool,
 * each read/search, and the final submit. A per-episode cap of 8 (the old default)
 * exhausted before an agent could finish a slice, and the run FAILED on the turn cap
 * instead of continuing to the next session. These raise the default to a productive
 * budget per kind; both stay fully editable in the builder's Advanced section.
 *
 * Kept in a pure module (no React/MUI imports) so it can be unit-tested directly in
 * the `node`-env web vitest runner without booting a component tree.
 */

/** Inbox agents: a triage → draft → (approval) send slice; default 30 turns. */
export const INBOX_MAX_TURNS_PER_EPISODE = 30;

/** Research agents: many search/read turns per session, so a higher default (~30). */
export const RESEARCH_MAX_TURNS_PER_EPISODE = 30;

/**
 * Short human labels for an agent's `kind` (§13.1/§13.3), shared by the builder's
 * header chip and the list page's row chip so the two never drift.
 */
export const AGENT_KIND_LABELS: Record<AgentKind, string> = {
  inbox: 'Inbox',
  research: 'Research',
  task: 'Task',
};

/**
 * The single source of truth for an agent kind's chip COLOR (§13.1/§13.3), shared by the
 * Agents list row, the Runs list row and the builder header so the same entity never
 * shows two colours across screens. Kept on-theme and subtle — graphite `default` for
 * inbox, cool-slate `secondary` for research, muted-blue `info` for task (never the loud
 * amber `warning`). Values are MUI `Chip` colours.
 */
export const KIND_CHIP_COLOR: Record<AgentKind, 'default' | 'secondary' | 'info'> = {
  inbox: 'default',
  research: 'secondary',
  task: 'info',
};
